import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { firestore } from '../../database/firebase';
import { COLLECTION_REGISTRY, getAllCollectionKeys } from '../config/collectionsRegistry';
import DataTable from '../components/DataTable';
import DetailDrawer from '../components/DetailDrawer';
import LoadingState from '../components/LoadingState';
import ErrorState from '../components/ErrorState';
import EmptyState from '../components/EmptyState';
import { canPerformActionOnCollection } from '../security/rbac';
import { useAuth } from '../../context/AuthContext';

const DevCollectionsExplorerPage = () => {
  const { currentUser } = useAuth();
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const userRoles = currentUser?.roles || [];

  useEffect(() => {
    if (selectedCollection) {
      loadCollectionData(selectedCollection);
    }
  }, [selectedCollection]);

  const loadCollectionData = async collectionKey => {
    try {
      setIsLoading(true);
      setError(null);

      const registryEntry = COLLECTION_REGISTRY[collectionKey];
      if (!registryEntry) {
        throw new Error(`Collection ${collectionKey} not found in registry`);
      }

      const collectionRef = collection(firestore, registryEntry.name);
      const q = query(
        collectionRef,
        orderBy(registryEntry.defaultSort.field, registryEntry.defaultSort.direction),
        limit(100)
      );

      const snapshot = await getDocs(q);
      const items = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));

      setData(items);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error loading collection data:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRowClick = row => {
    setSelectedRow(row);
    setDrawerOpen(true);
  };

  const getTableColumns = () => {
    if (!selectedCollection) return [];

    const registryEntry = COLLECTION_REGISTRY[selectedCollection];
    if (!registryEntry) return [];

    // Generate columns from searchable fields + common fields
    const columns = [
      { key: 'id', label: 'ID', sortable: true },
      ...registryEntry.searchableFields.map(field => ({
        key: field,
        label: field.charAt(0).toUpperCase() + field.slice(1),
        sortable: true,
      })),
      {
        key: 'createdAt',
        label: 'Created',
        sortable: true,
        render: val => {
          if (!val) return '—';
          const date = val?.toDate ? val.toDate() : new Date(val);
          return date.toLocaleString();
        },
      },
    ];

    return columns;
  };

  const canEdit = selectedCollection
    ? canPerformActionOnCollection(userRoles, selectedCollection, 'update')
    : false;

  return (
    <div className="flex h-full">
      <div className="flex flex-1 flex-col gap-4 p-6">
        <header>
          <h1 className="text-2xl font-semibold text-white">Collections Explorer</h1>
          <p className="text-sm text-slate-400">Browse and inspect database collections</p>
        </header>

        {/* Collection Selector */}
        <div className="flex gap-4">
          <select
            value={selectedCollection || ''}
            onChange={e => setSelectedCollection(e.target.value)}
            className="rounded-md border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-white focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="">Select a collection...</option>
            {getAllCollectionKeys().map(key => {
              const entry = COLLECTION_REGISTRY[key];
              return (
                <option key={key} value={key}>
                  {entry.label} ({entry.name})
                </option>
              );
            })}
          </select>
        </div>

        {/* Data Table */}
        {selectedCollection && (
          <>
            {isLoading ? (
              <LoadingState message="Loading collection data..." />
            ) : error ? (
              <ErrorState message={error} onRetry={() => loadCollectionData(selectedCollection)} />
            ) : data.length === 0 ? (
              <EmptyState message="No data found in this collection" />
            ) : (
              <DataTable
                data={data}
                columns={getTableColumns()}
                onRowClick={handleRowClick}
                isLoading={isLoading}
                emptyMessage="No data available"
              />
            )}
          </>
        )}

        {!selectedCollection && <EmptyState message="Select a collection to explore its data" />}
      </div>

      {/* Detail Drawer */}
      <DetailDrawer
        isOpen={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedRow(null);
        }}
        title={selectedRow ? `Details: ${selectedRow.id}` : 'Details'}
        width="w-96"
      >
        {selectedRow && (
          <div className="space-y-4">
            {Object.entries(selectedRow).map(([key, value]) => (
              <div key={key}>
                <p className="text-xs font-semibold text-slate-400">{key}</p>
                <p className="mt-1 text-sm text-slate-200">
                  {value === null || value === undefined
                    ? '—'
                    : typeof value === 'object' && value?.toDate
                      ? value.toDate().toLocaleString()
                      : typeof value === 'object'
                        ? JSON.stringify(value, null, 2)
                        : String(value)}
                </p>
              </div>
            ))}
            {canEdit && (
              <div className="mt-6 flex gap-2">
                <button className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">
                  Edit
                </button>
              </div>
            )}
          </div>
        )}
      </DetailDrawer>
    </div>
  );
};

export default DevCollectionsExplorerPage;
